import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../api/client'
import { settingsApi } from '../api/settings'
import type { TenantSettings } from '../api/types'
import { Alert } from '../components/Alert'
import { BbSwitch } from '../components/BbSwitch'
import { ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'

/* Ported from "batchbooks_preferences_integrated".

   The prototype draws Date Format plus seven toggles, but only two of those
   seven exist in TenantSettings. Auto-create for Class, Employees, Location and
   Payment Method have no fields, and neither does "does your data contain
   updates to existing QuickBooks data?" (an upsert mode the contract has no
   concept of) — all five are omitted rather than rendered as switches that
   would silently do nothing.

   Going the other way, the contract carries nine settings the prototype never
   draws. Dropping them would have made real behaviour unreachable, so they
   continue below in the same row idiom. This page replaces the old read-only
   summary and its edit modal: everything is directly editable now. */

/* .NET custom-format strings, not the lowercase shapes the prototype drew. The
   value travels straight into DateTime.TryParseExact, where 'mm' means minutes:
   a lowercase month would parse nothing and fail every date column in the file. */
/* .NET custom-format strings, not the lowercase shapes the prototype drew. The
   value travels straight into DateTime.TryParseExact, where 'mm' means minutes:
   a lowercase month would parse nothing and fail every date column in the file. */
const DATE_FORMATS = ['yyyy-MM-dd', 'dd/MM/yyyy', 'MM/dd/yyyy', 'dd-MMM-yyyy']
const SEPARATORS = [
  { value: '.', label: 'Full stop  (1,234.56)' },
  { value: ',', label: 'Comma  (1.234,56)' },
]

/* QuickBooks' account types, mirroring the list on the Item Settings page. The
   subset a spreadsheet-named account could plausibly be: nothing here is a
   bank, receivable, payable or equity account, because those are decisions with
   consequences a bulk import should not be making on the user's behalf. */
const ACCOUNT_TYPES = ['Expense', 'Income', 'Cost of Goods Sold', 'Other Expense', 'Other Income']

type Draft = TenantSettings

export function SettingsPage() {
  const loaded = useAsync<TenantSettings>(() => settingsApi.get(), [])
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Seed the editable copy the first time the fetch lands, and again if the
  // page is reloaded. Keyed on the fetched object identity rather than a
  // setState-in-effect, which oxlint rightly flags.
  const [seededFrom, setSeededFrom] = useState<TenantSettings | null>(null)
  if (loaded.data && loaded.data !== seededFrom) {
    setSeededFrom(loaded.data)
    setDraft(loaded.data)
  }

  if (loaded.loading && !draft) return <LoadingState label="Loading preferences…" />
  if (loaded.error && !draft) return <ErrorState error={loaded.error} onRetry={loaded.reload} />
  if (!draft) return null

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev))
    setSaved(false)
  }

  const toggle = (key: keyof Draft, label: string, help?: string) => (
    <div className="bb-opt" key={String(key)}>
      <span className="bb-opt-l">
        <span className="bb-opt-dot" />
        <span>
          <span className="bb-opt-t">{label}</span>
          {help ? <span className="bb-opt-help">{help}</span> : null}
        </span>
      </span>
      <span className="bb-opt-c">
        <BbSwitch
          id={String(key)}
          label={label}
          checked={draft[key] as boolean}
          onChange={(next) => set(key, next as Draft[typeof key])}
        />
      </span>
    </div>
  )

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const next = await settingsApi.update(draft)
      setDraft(next)
      setSaved(true)
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not reach the server.')
    } finally {
      setSaving(false)
    }
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(loaded.data)

  return (
    <div className="bb bb-page">
      <h1>Settings</h1>
      <div className="bb-tabs">
        <span className="bb-tab bb-tab-on">Preferences</span>
        <Link className="bb-tab" to="/app/settings/items">
          Item Settings
        </Link>
      </div>

      {error ? (
        <Alert tone="err" title="Could not save your preferences">
          {error}
        </Alert>
      ) : null}
      {saved ? <Alert tone="ok" title="Preferences saved" /> : null}

      <div className="bb-sec">
        <h2 className="bb-sec-h">Default Preferences</h2>

        <div className="bb-opt">
          <span className="bb-opt-l">
            <span className="bb-opt-dot" />
            <span>
              <label className="bb-opt-t" htmlFor="dateFormat">
                Date Format
              </label>
              <span className="bb-opt-help">How dates are read out of your uploaded file.</span>
            </span>
          </span>
          <span className="bb-opt-c">
            <select
              className="bb-select"
              id="dateFormat"
              value={draft.dateFormat}
              onChange={(event) => set('dateFormat', event.target.value)}
            >
              {(DATE_FORMATS.includes(draft.dateFormat)
                ? DATE_FORMATS
                : [draft.dateFormat, ...DATE_FORMATS]
              ).map((format) => (
                <option value={format} key={format}>
                  {format}
                </option>
              ))}
            </select>
          </span>
        </div>

        <div className="bb-opt">
          <span className="bb-opt-l">
            <span className="bb-opt-dot" />
            <span>
              <label className="bb-opt-t" htmlFor="decimalSeparator">
                Decimal separator
              </label>
            </span>
          </span>
          <span className="bb-opt-c">
            <select
              className="bb-select"
              id="decimalSeparator"
              value={draft.decimalSeparator}
              onChange={(event) => set('decimalSeparator', event.target.value)}
            >
              {SEPARATORS.map((sep) => (
                <option value={sep.value} key={sep.value}>
                  {sep.label}
                </option>
              ))}
            </select>
          </span>
        </div>
      </div>

      <div className="bb-sec">
        <h2 className="bb-sec-h">Import Options</h2>
        {toggle(
          'autoCreateCustomers',
          'Do you want Customers to be auto created if these are not there in QuickBooks?',
        )}
        {toggle(
          'autoCreateVendors',
          'Do you want Vendors to be auto created if these are not there in QuickBooks?',
        )}
        {toggle(
          'autoCreateItems',
          'Do you want Products and Services to be auto created if these are not there in QuickBooks?',
        )}
        {toggle(
          'autoCreateAccounts',
          'Do you want Accounts to be auto created if these are not there in QuickBooks?',
        )}
        {toggle(
          'autoCreateTaxAgencies',
          'Do you want Tax Agencies to be auto created if these are not there in QuickBooks?',
        )}

        <div className="bb-opt">
          <span className="bb-opt-l">
            <span className="bb-opt-dot" />
            <span>
              <label className="bb-opt-t" htmlFor="defaultAccountType">
                Account type for accounts created above
              </label>
              <span className="bb-opt-help">
                QuickBooks refuses an account with no type, and a spreadsheet column cannot
                supply one. Leave this blank and Accounts are never created automatically.
              </span>
            </span>
          </span>
          <span className="bb-opt-c">
            <select
              className="bb-select"
              id="defaultAccountType"
              value={draft.defaultAccountType ?? ''}
              onChange={(event) => set('defaultAccountType', event.target.value || null)}
            >
              <option value="">Do not create accounts</option>
              {ACCOUNT_TYPES.map((type) => (
                <option value={type} key={type}>
                  {type}
                </option>
              ))}
            </select>
          </span>
        </div>

        <div className="bb-opt">
          <span className="bb-opt-l">
            <span className="bb-opt-dot" />
            <span>
              <label className="bb-opt-t" htmlFor="defaultItemIncomeAccount">
                Income account for products &amp; services created above
              </label>
              <span className="bb-opt-help">
                QuickBooks refuses a product or service with no income or expense account, and a
                spreadsheet column cannot supply one. Type the exact name of an account that
                already exists in QuickBooks' own chart of accounts. Leave this blank and
                Products &amp; Services are never created automatically, even with the toggle on.
              </span>
            </span>
          </span>
          <span className="bb-opt-c">
            <input
              className="bb-input"
              id="defaultItemIncomeAccount"
              type="text"
              placeholder="e.g. Sales of Product Income"
              value={draft.defaultItemIncomeAccount ?? ''}
              onChange={(event) => set('defaultItemIncomeAccount', event.target.value || null)}
            />
          </span>
        </div>
      </div>

      <div className="bb-sec">
        <h2 className="bb-sec-h">File parsing</h2>
        {toggle('firstRowIsHeader', 'Treat the first row as a header row')}
        {toggle('trimWhitespace', 'Trim leading and trailing whitespace from every cell')}

        <div className="bb-opt">
          <span className="bb-opt-l">
            <span className="bb-opt-dot" />
            <span>
              <label className="bb-opt-t" htmlFor="batchSize">
                Batch size
              </label>
              <span className="bb-opt-help">Rows sent to QuickBooks per batch request.</span>
            </span>
          </span>
          <span className="bb-opt-c">
            <input
              className="bb-input"
              id="batchSize"
              type="number"
              min={1}
              value={draft.batchSize}
              onChange={(event) => set('batchSize', Number(event.target.value))}
            />
          </span>
        </div>
      </div>

      <div className="bb-sec">
        <h2 className="bb-sec-h">Safety</h2>
        {toggle('skipDuplicateDocNumbers', 'Skip rows whose document number already exists in QuickBooks')}
        {toggle('skipInFileDuplicates', 'Skip rows duplicated within the uploaded file')}
        {toggle('allowNegativeQuantities', 'Allow negative quantities')}
        {toggle('allowClosedPeriodDates', 'Allow dates that fall in a closed period')}

        <div className="bb-opt">
          <span className="bb-opt-l">
            <span className="bb-opt-dot" />
            <span>
              <label className="bb-opt-t" htmlFor="stopOnError">
                Stop the job above this error rate
              </label>
              <span className="bb-opt-help">Leave empty to never stop early.</span>
            </span>
          </span>
          <span className="bb-opt-c">
            <input
              className="bb-input"
              id="stopOnError"
              type="number"
              min={0}
              max={100}
              placeholder="Never"
              value={draft.stopOnErrorRatePercent ?? ''}
              onChange={(event) =>
                set(
                  'stopOnErrorRatePercent',
                  event.target.value === '' ? null : Number(event.target.value),
                )
              }
            />
          </span>
        </div>

        <div className="bb-opt">
          <span className="bb-opt-l">
            <span className="bb-opt-dot" />
            <span>
              <label className="bb-opt-t" htmlFor="tolerance">
                Amount tolerance
              </label>
              <span className="bb-opt-help">
                How far a computed total may drift from the file before the row fails.
              </span>
            </span>
          </span>
          <span className="bb-opt-c">
            <input
              className="bb-input"
              id="tolerance"
              type="number"
              step="0.01"
              min={0}
              value={draft.amountTolerance}
              onChange={(event) => set('amountTolerance', Number(event.target.value))}
            />
          </span>
        </div>
      </div>

      <div className="bb-wizard-foot">
        <span className="bb-map-help">
          These apply to every import in this workspace, including ones from a saved mapping.
          Auto-create for Class, Employees, Location and Payment Method has no field in the
          settings contract yet, so it is not shown here.
        </span>
        <span className="bb-wizard-foot-r">
          <button
            className="bb-btn bb-btn-quiet"
            type="button"
            disabled={!dirty || saving}
            onClick={() => {
              if (loaded.data) setDraft(loaded.data)
              setSaved(false)
            }}
          >
            Cancel
          </button>
          <button
            className="bb-btn bb-btn-green"
            type="button"
            disabled={!dirty || saving}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </span>
      </div>
    </div>
  )
}
